import CampaignStatusValue from '@/Enums/CampaignManagement/CampaignStatus.enum';
import CampaignsTableColumns from '@/Enums/CampaignManagement/CampaignsTableColumns.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import ArchivedChip from '@/components/Grid/ArchivedChip';
import AudienceGroups from '@/components/Grid/AudienceGroups';
import GeographyChips from '@/components/Grid/GeographyChips';
import PacingStatus from '@/components/Grid/PacingStatus';
import { CAMPAIGN_MANAGEMENT_ROW_TYPE } from '@/constants/campaignManagement';
import { CAMPAIGN_MANAGEMENT_COLUMNS } from '@/constants/sessionStorage';
import { defaultQuickFilter } from '@/helpers/grid/quickFilter';
import { fallbackValueFormatter } from '@/helpers/grid/valueFormatter.helpers';
import { pacingStatusOnlyOperators } from '@/helpers/pacing';
import { dateToFormat, dateToShortFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import { numberToPercent, numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCellCheckboxRenderer,
  getGridStringQuickFilterFn,
} from '@mui/x-data-grid-pro';
import ArchiveButton from './components/ArchiveButton';
import CampaignStatus from './components/CampaignStatus';
import EndDateInput from './components/EndDateInput';
import ImpressionsBookedInput from './components/ImpressionsBookedInput';
import StatusToggle from './components/StatusToggle';

export const getColumns = (isEditMode, isAdmin = false, isDV360CampaignManagementEnabled = false) => {
  const columns = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      display: 'flex',
      renderCell: params => {
        const { rowNode } = params;

        if (!rowNode.depth) {
          return <GridCellCheckboxRenderer {...params} />;
        }

        return null;
      },
    },
    {
      field: CampaignsTableColumns.ALT_ID,
      headerName: 'External ID / Billing ID',
      valueFormatter: fallbackValueFormatter,
      getApplyQuickFilterFn: getGridStringQuickFilterFn,
    },
    {
      field: CampaignsTableColumns.ADVERTISER,
      headerName: 'Advertiser',
      width: 200,
      valueFormatter: fallbackValueFormatter,
      getApplyQuickFilterFn: getGridStringQuickFilterFn,
    },
    {
      field: CampaignsTableColumns.WHITELABEL,
      headerName: 'Whitelabel',
      width: 200,
      valueFormatter: fallbackValueFormatter,
      getApplyQuickFilterFn: getGridStringQuickFilterFn,
    },
    ...(isDV360CampaignManagementEnabled
      ? [
          {
            field: CampaignsTableColumns.PLATFORM,
            headerName: 'Platform',
            align: 'center',
            headerAlign: 'center',
          },
        ]
      : []),
    {
      field: CampaignsTableColumns.STATUS,
      headerName: 'Status',
      align: 'center',
      headerAlign: 'center',
      width: 170,
      display: 'flex',
      renderCell: ({ value, row }) => {
        if (row.isArchived) {
          return <ArchivedChip />;
        }
        const showToggle =
          isEditMode &&
          row?.type !== CAMPAIGN_MANAGEMENT_ROW_TYPE.FLIGHT &&
          ![CampaignStatusValue.DRAFT, CampaignStatusValue.APPROVAL_REQUIRED].includes(value);
        return showToggle ? (
          <StatusToggle defaultValue={value} row={row} />
        ) : (
          <CampaignStatus campaignId={row?.campaignId} status={value} isEditMode={isEditMode} />
        );
      },
    },
    {
      field: CampaignsTableColumns.START_DATE,
      headerName: 'Start Date',
      align: 'center',
      headerAlign: 'center',
      width: 150,
      valueFormatter: (value, row) =>
        row?.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM || row?.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN
          ? dateToFormat(value, DateFormat.NumericMonthDayYearTime)
          : dateToShortFormat(value),
    },
    {
      field: CampaignsTableColumns.END_DATE,
      headerName: 'End Date',
      align: 'center',
      headerAlign: 'center',
      width: isEditMode ? 200 : 180,
      valueFormatter: (value, row) =>
        row?.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM || row?.type === CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN
          ? dateToFormat(value, DateFormat.NumericMonthDayYearTime)
          : dateToShortFormat(value),
      display: 'flex',
      renderCell: params => {
        const { value, formattedValue, row } = params;
        if (!row) {
          return formattedValue;
        }
        const { campaignId, lineItemId, type, startDate, status, platform } = row;
        const allowEdit =
          isEditMode && type !== CAMPAIGN_MANAGEMENT_ROW_TYPE.FLIGHT && status !== CampaignStatusValue.DRAFT;
        return allowEdit ? (
          <EndDateInput
            defaultValue={value}
            campaignId={campaignId}
            lineItemId={lineItemId}
            minDate={startDate}
            platform={platform}
          />
        ) : (
          formattedValue
        );
      },
    },
    {
      field: CampaignsTableColumns.LINE_ITEMS,
      headerName: 'Line Items',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: CampaignsTableColumns.CREATIVES,
      headerName: 'Creatives',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'channel',
      headerName: 'Channel',
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueFormatter: fallbackValueFormatter,
    },
    {
      field: CampaignsTableColumns.PACING_STATUS,
      headerName: 'Pacing Status',
      align: 'center',
      headerAlign: 'center',
      width: 115,
      display: 'flex',
      renderCell: PacingStatus,
      valueGetter: (_value, row) => row.pacingStatus,
      filterOperators: pacingStatusOnlyOperators,
    },
    {
      field: CampaignsTableColumns.PACING_TO,
      headerName: 'Pacing To %',
      align: 'center',
      headerAlign: 'center',
      valueFormatter: value => (value ? numberToPercent(value) : '—'),
    },
    {
      field: CampaignsTableColumns.BOOKED,
      headerName: 'Booked',
      align: 'center',
      headerAlign: 'center',
      width: isEditMode ? 150 : 100,
      valueFormatter: value => (value ? numberToUsFormat(value) : '—'),
      display: 'flex',
      renderCell: ({ value, formattedValue, row: { campaignId, lineItemId, type } }) => {
        return isEditMode &&
          ![CAMPAIGN_MANAGEMENT_ROW_TYPE.FLIGHT, CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN].includes(type) ? (
          <ImpressionsBookedInput defaultValue={value} campaignId={campaignId} lineItemId={lineItemId} />
        ) : (
          formattedValue
        );
      },
    },
    {
      field: CampaignsTableColumns.DELIVERED,
      headerName: 'Delivered',
      align: 'center',
      headerAlign: 'center',
      valueFormatter: value => (value ? numberToUsFormat(value) : '—'),
    },
    {
      field: CampaignsTableColumns.LAST_UPDATED,
      headerName: 'Last Updated',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      valueFormatter: value => dateToShortFormat(value),
    },
    {
      field: 'product',
      headerName: 'Product',
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueFormatter: fallbackValueFormatter,
    },
    {
      field: 'geo',
      headerName: 'Geography',
      headerAlign: 'center',
      sortable: false,
      width: 220,
      display: 'flex',
      renderCell: GeographyChips,
    },
    {
      field: 'frequencyCaps',
      headerName: 'Frequency Caps',
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      valueFormatter: value =>
        value
          ? Object.keys(value)
              .map(key => `${key}: ${value[key]}`)
              .join(', ')
          : '—',
      display: 'flex',
      renderCell: ({ formattedValue }) => <span className='capitalize'>{formattedValue}</span>,
    },
    {
      field: 'audience',
      headerName: 'Audience',
      align: 'center',
      sortable: false,
      width: 90,
      display: 'flex',
      renderCell: ({ value, row: { channel } }) => <AudienceGroups value={value?.[channel]} />,
    },
    ...(isAdmin
      ? [
          {
            field: 'action',
            headerName: 'Action',
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            width: 100,
            display: 'flex',
            renderCell: ArchiveButton,
          },
        ]
      : []),
  ].map((item, _order) => ({
    ...item,
    _order,
    getApplyQuickFilterFn: item.getApplyQuickFilterFn || defaultQuickFilter,
  }));

  try {
    const savedOrder = sessionStorage.getItem(CAMPAIGN_MANAGEMENT_COLUMNS.ORDER).split(',');
    if (!savedOrder.length) return columns;
    return savedOrder.map(order => columns[order]).filter(column => column !== undefined);
  } catch {
    return columns;
  }
};
